import React, { useEffect, useRef, useState } from "react";
import { CloseButton } from "react-bootstrap";

import {
    draggable,
    dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import classNames from "classnames";
import invariant from "tiny-invariant";

import { useTournamentContext } from "../contexts/Tournament";
import { reOrderPhotos } from "../lib/reorderPhotos";
import { PhotoData } from "../types/tournament";

import { CONTENT_IMAGE_URL } from "../../../../../lib/globals";

interface Props {
    photo: PhotoData;
    idx: number;
    setModalShow: React.Dispatch<React.SetStateAction<boolean>>;
    setModalContent: React.Dispatch<React.SetStateAction<string>>;
}

const PhotoRow = (props: Props): React.JSX.Element => {
    const { setCurrentPhotos } = useTournamentContext();
    const [dragging, setDragging] = useState<boolean>(false);
    const [isDraggedOver, setIsDraggedOver] = useState(false);

    const {
        photo: { photo, order },
        idx,
        setModalShow,
        setModalContent,
    } = props;

    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;

        invariant(el, "No Element");

        return draggable({
            element: el,
            getInitialData: () => ({ order, photo }),
            onDragStart: () => setDragging(true),
            onDrop: () => setDragging(false),
        });
    }, [order, photo]);

    useEffect(() => {
        const el = ref.current;
        invariant(el);

        return dropTargetForElements({
            element: el,
            getData: () => ({ order, photo }),
            onDragEnter: () => setIsDraggedOver(true),
            onDragLeave: () => setIsDraggedOver(false),
            onDrop: () => setIsDraggedOver(false),
        });
    }, [order, photo]);

    return (
        <>
            <tr
                key={idx}
                ref={ref}
                className={classNames({
                    photoRow: true,
                    dragOver: isDraggedOver,
                    dragging: dragging,
                })}
            >
                <td>{order}</td>
                <td>
                    <img
                        className="thumbnail"
                        src={`${CONTENT_IMAGE_URL}tournamentPhotos/${photo}`}
                        onClick={() => {
                            setModalContent(photo);
                            setModalShow(true);
                        }}
                    />
                </td>
                <td>{photo}</td>
                <td>
                    <CloseButton
                        key={idx + ":del"}
                        onClick={() =>
                            setCurrentPhotos(prev => {
                                invariant(prev);
                                const updatedPhotos = [
                                    ...prev.filter(i => i.photo !== photo),
                                ];
                                return reOrderPhotos(updatedPhotos);
                            })
                        }
                    />
                </td>
            </tr>
        </>
    );
};

export default PhotoRow;
